import { apiFetch } from '@/api/client'
import { parseCurrencyType, type CurrencyType } from '@/lib/currency'

export type CurrencyRow = {
  _id: string
  name: string
  rate: number
}

type GetCurrenciesResponse = {
  value: boolean
  data: CurrencyRow[]
}

export const FIXTURE_CURRENCIES: CurrencyRow[] = [
  { _id: 'INR', name: 'INR', rate: 1 },
  { _id: 'POINTS', name: 'POINTS', rate: 100 },
]

export function rowsFromGetCurrencies(response: GetCurrenciesResponse): CurrencyRow[] {
  if (!response.value) return []
  return (response.data ?? []).filter(
    (row) =>
      Boolean(row._id && row.name) &&
      Number.isFinite(row.rate) &&
      row.rate > 0,
  )
}

export async function getCurrencies(): Promise<CurrencyRow[]> {
  const response = await apiFetch<GetCurrenciesResponse>('/v1/currency/getCurrencies', {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return rowsFromGetCurrencies(response)
}

export type MemberBalance = {
  balance: number
  available: number
  creditLimit: number
  distributed: number
  exposure: number
  balanceUp: number
  currency: CurrencyType
}

type GetMemberBalanceResponse = {
  value: boolean
  data?: {
    balanceMinor?: string
    availableMinor?: string
    creditLimitMinor?: string
    distributedMinor?: string
    exposureMinor?: string
    balanceUpMinor?: string
    currencyType?: string
  }
}

const fromMinorSigned = (raw: string | undefined): number => {
  const value = Number(raw ?? 0)
  return Number.isFinite(value) ? value / 100 : 0
}

export function memberBalanceFromApi(response: GetMemberBalanceResponse): MemberBalance | null {
  if (!response.value || !response.data) return null
  return {
    balance: fromMinorSigned(response.data.balanceMinor),
    available: fromMinorSigned(response.data.availableMinor),
    creditLimit: fromMinorSigned(response.data.creditLimitMinor),
    distributed: fromMinorSigned(response.data.distributedMinor),
    exposure: fromMinorSigned(response.data.exposureMinor),
    balanceUp: fromMinorSigned(response.data.balanceUpMinor),
    currency: parseCurrencyType(response.data.currencyType),
  }
}

export async function getMemberBalance(): Promise<MemberBalance | null> {
  const response = await apiFetch<GetMemberBalanceResponse>('/v1/member/getBalance', {
    method: 'POST',
    body: JSON.stringify({}),
  })
  return memberBalanceFromApi(response)
}
